/**
 * AI が出した変更案の確認カード
 *
 * 表示する差分はサーバーが保存した変更案から計算したもの（AI の文章ではない）。
 * 「確定する」を押したときだけ、サーバーは保存済みの内容をそのまま実行する。
 * 参加者へお知らせが届く変更は、ConfirmDialog でもう一度確かめてから確定する。
 */

import { useState } from 'react';
import { ExclamationTriangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { CHAT_ACTION_STATUS, CHAT_ACTION_STATUS_LABEL } from '@/constants/chat';
import type { ChatPendingAction } from '@/services/chat';

interface ChatActionCardProps {
  action: ChatPendingAction;
  /** null は確定待ち以外（過去の提案）。確定待ちのときだけボタンを出す */
  status: string | null | undefined;
  isActive: boolean;
  busy: boolean;
  onConfirm: (acknowledgeNotification: boolean) => void;
  onCancel: () => void;
}

function formatDeadline(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
}

export default function ChatActionCard({
  action,
  status,
  isActive,
  busy,
  onConfirm,
  onCancel,
}: ChatActionCardProps) {
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const done = status && status !== CHAT_ACTION_STATUS.PENDING;
  const showButtons = isActive && !done;

  const handleConfirm = () => {
    if (action.notifiesParticipants) {
      setNotifyDialogOpen(true);
      return;
    }
    onConfirm(false);
  };

  return (
    <div className="mt-2 rounded-xl border border-primary-200 bg-primary-50/60 p-3 text-sm">
      <p className="font-semibold text-text-900">{action.summary}</p>
      {action.eventTitle && action.eventId && (
        <p className="mt-0.5 text-xs text-text-600">対象: {action.eventTitle}</p>
      )}

      <dl className="mt-2 space-y-1.5">
        {(action.changes ?? []).map((change) => (
          <div key={change.label} className="grid grid-cols-[4.5rem_1fr] gap-2">
            <dt className="text-xs text-text-500 pt-0.5">{change.label}</dt>
            <dd className="min-w-0 break-words text-text-800">
              {change.before != null && (
                <span className="block text-xs text-text-400 line-through">{change.before}</span>
              )}
              <span>{change.after}</span>
            </dd>
          </div>
        ))}
      </dl>

      {action.afterUntrustedContent && (
        <p className="mt-2 flex gap-1.5 rounded-lg bg-warning-50 p-2 text-xs text-warning-800">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            参加者が書いた文章を読んだあとの提案です。頼んでいない変更が混ざっていないか確かめてください。
          </span>
        </p>
      )}
      {action.notifiesParticipants && (
        <p className="mt-2 flex gap-1.5 text-xs text-text-700">
          <InformationCircleIcon className="h-4 w-4 flex-shrink-0 text-primary-600" />
          <span>
            確定すると、参加者
            {action.notifiedParticipantCount > 0 ? `${action.notifiedParticipantCount}人` : ''}
            に変更のお知らせが届きます。
          </span>
        </p>
      )}

      {showButtons ? (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-x-2 gap-y-1.5">
          <span className="mr-auto whitespace-nowrap text-xs text-text-500">
            {formatDeadline(action.expiresAt)}まで確定できます
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="whitespace-nowrap rounded-lg px-3 py-1.5 text-sm text-text-600 hover:bg-text-100 disabled:opacity-50"
            >
              取り消す
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={busy}
              className="whitespace-nowrap rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              確定する
            </button>
          </div>
        </div>
      ) : (
        done && (
          <p className="mt-2 text-xs font-semibold text-text-500">
            {CHAT_ACTION_STATUS_LABEL[status] ?? ''}
          </p>
        )
      )}

      <ConfirmDialog
        open={notifyDialogOpen}
        title="参加者にお知らせが届きます"
        message="この変更を保存すると、参加者に変更のお知らせが送られます。"
        impactItems={[
          action.notifiedParticipantCount > 0
            ? `参加者${action.notifiedParticipantCount}人`
            : '参加している人',
        ]}
        impactLabel="お知らせが届く人:"
        confirmText="保存して知らせる"
        cancelText="やめる"
        variant="primary"
        onConfirm={() => {
          setNotifyDialogOpen(false);
          onConfirm(true);
        }}
        onCancel={() => setNotifyDialogOpen(false)}
      />
    </div>
  );
}
